import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';

import { USER_PROPERTIES } from '@app/constants/endpoints';
import type { LocaleValue } from '@app/constants/locale-options';
import { QUERY_KEYS } from '@app/constants/service';
import { fetchData, putData } from '@app/hooks/react-query-helpers';
import i18n from '@app/i18n';
import { describeError } from '@app/i18n/describe-error';
import { StateType } from '@app/types/reducer-state';
import { toaster } from '@chakra-primitives/toaster';

import { InboxNotificationBehavior } from './admin';

type UserProperties = {
  inboxBehavior?: InboxNotificationBehavior;
  inboxBehaviorLockedByAdmin?: boolean;
  language?: LocaleValue;
  ignoreInternalUnreadMessages?: boolean;
};

export const useUserProperties = (
  userId: string,
  options?: { enabled?: boolean },
): UseQueryResult<UserProperties, Error> =>
  useQuery({
    queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
    queryFn: () => fetchData(`${USER_PROPERTIES}?userId=${userId}`),
    enabled: !!userId && (options?.enabled ?? true),
  });

export const useUpdateUserProperties = (
  userId: string,
  options?: { disableToasts?: boolean },
): UseMutationResult<StateType, Error, UserProperties> => {
  const queryClient = useQueryClient();
  const disableToasts = options?.disableToasts ?? false;

  return useMutation({
    mutationFn: async (data: UserProperties) =>
      putData(
        `${USER_PROPERTIES}?userId=${userId}`,
        data,
      ) as Promise<StateType>,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
      });
      if (!disableToasts) {
        toaster.create({
          title: i18n.t('toasts:admin.userPropertiesUpdated.title'),
          type: 'success',
        });
      }
    },
    onError: (error: Error) => {
      if (!disableToasts) {
        toaster.create({
          title: i18n.t('toasts:admin.userPropertiesUpdateFailed.title'),
          description: describeError(error, i18n.t('common:tryAgainLater')),
          type: 'error',
        });
      }
    },
  });
};
